import { PDFDocument, PDFFont, StandardFonts } from 'pdf-lib';

export type FontStyleKey = `${string}-${'normal'|'italic'}-${number}`; // family-style-weight

export interface FontConfig {
  family: string;
  weight: number; // 100..900
  style: 'normal' | 'italic';
  data?: ArrayBuffer; // optional; when absent fallback to StandardFonts
}

export class FontManager {
  private cache: Map<FontStyleKey, PDFFont> = new Map();
  private sources: Map<FontStyleKey, ArrayBuffer | 'STANDARD_HELVETICA'> = new Map();

  constructor(configs?: FontConfig[]) {
    if (configs && configs.length > 0) {
      for (const c of configs) {
        const key: FontStyleKey = `${c.family}-${c.style}-${c.weight}` as FontStyleKey;
        if (c.data) {
          this.sources.set(key, c.data);
        }
      }
    }
  }

  private keyFor(family?: string, italic?: boolean, weight?: number): FontStyleKey {
    return `${family || 'Helvetica'}-${italic ? 'italic' : 'normal'}-${weight || 400}` as FontStyleKey;
  }

  private findNearestSource(family?: string, italic?: boolean, weight?: number): { key: FontStyleKey; data?: ArrayBuffer } | null {
    const desired: FontStyleKey = this.keyFor(family, italic, weight);
    if (this.sources.has(desired)) return { key: desired, data: this.sources.get(desired) as ArrayBuffer };
    // Fallbacks: same family/style, nearest weight
    const style = italic ? 'italic' : 'normal';
    const fam = family || 'Helvetica';
    let best: { key: FontStyleKey; data: ArrayBuffer } | null = null;
    let bestDelta = Number.MAX_SAFE_INTEGER;
    for (const [k, data] of this.sources.entries()) {
      const [kf, ks, kwStr] = k.split('-');
      if (kf !== fam || ks !== style) continue;
      const kw = Number(kwStr);
      const d = Math.abs((weight || 400) - kw);
      if (d < bestDelta) {
        best = { key: k as FontStyleKey, data: data as ArrayBuffer };
        bestDelta = d;
      }
    }
    return best;
  }

  async getFont(doc: PDFDocument, family?: string, weight?: number, italic?: boolean): Promise<PDFFont> {
    const key = this.keyFor(family, italic, weight);
    const cached = this.cache.get(key);
    if (cached) return cached;

    // Find source data; fallback to Helvetica
    const src = this.findNearestSource(family, italic, weight);
    let font: PDFFont;
    if (src && src.data) {
      // Subset true by default in pdf-lib when drawing; embedding once is enough
      font = await doc.embedFont(src.data, { subset: true });
      this.cache.set(key, font);
      return font;
    }
    font = await doc.embedFont(StandardFonts.Helvetica);
    this.cache.set(key, font);
    return font;
  }
}


